import type { Device } from '@/pages/Device/Device'
import {
  RCS_3000,
  RDI_3000,
  RSS_3000,
  RSS_3000_PC,
  ZKL_3000,
  ZKL_3000_RC,
  ZKL_3000_RCC,
  ZKL_3000_RC_AFTC,
} from '@/pages/Device/SupportedTypes'
import type { Ba, ResistanceBox } from './calibration'
import type { Composition } from './composition'
import { JSX } from 'react'
import { Alert } from '@mui/material'
import { useTranslation } from 'react-i18next'
import RdiVerifyStep from '@/pages/calibration/verify/RDIVerifyStep'
import RssVerifyStep from '@/pages/calibration/verify/RSSVerifyStep'
import RcsVerifyStep from '@/pages/calibration/verify/RCSVerifyStep'
import ZklrcVerifyStep from '@/pages/calibration/verify/ZKLVerifyStep'

export interface VerificationStepProps {
  device: Device | null
  setActiveStep: (step: number) => void
  composition: Composition | null
  setComposition: (c: Composition | null) => void
  resistanceBox: ResistanceBox | null
  setResistanceBox: (rb: ResistanceBox | null) => void
  jumperBox: string | null
  setJumperBox: (jb: string | null) => void
  ba: Ba
  setBa: (ba: Ba) => void
  dropOff: string
  setDropOff: (d: string) => void
  finishSelfTest: (message: Record<string, unknown>) => void
  lastJsonMessage: Record<string, unknown> | null
}

const VerificationStep = (props: VerificationStepProps): JSX.Element | null => {
  const { t } = useTranslation('app')
  const { device, ...rest } = props
  if (!device) return null

  switch (device.type) {
    case RDI_3000:
      return (
        <RdiVerifyStep
          device={device}
          composition={rest.composition}
          setComposition={rest.setComposition}
          setActiveStep={rest.setActiveStep}
          finishSelfTest={rest.finishSelfTest}
          lastJsonMessage={rest.lastJsonMessage}
        />
      )
    case RSS_3000:
    case RSS_3000_PC:
      return (
        <RssVerifyStep
          device={device}
          composition={rest.composition}
          setComposition={rest.setComposition}
          resistanceBox={rest.resistanceBox}
          setResistanceBox={rest.setResistanceBox}
          setActiveStep={rest.setActiveStep}
          finishSelfTest={rest.finishSelfTest}
          lastJsonMessage={rest.lastJsonMessage}
        />
      )
    case RCS_3000:
      return (
        <RcsVerifyStep
          device={device}
          composition={rest.composition}
          setComposition={rest.setComposition}
          setActiveStep={rest.setActiveStep}
          finishSelfTest={rest.finishSelfTest}
          lastJsonMessage={rest.lastJsonMessage}
        />
      )
    case ZKL_3000:
    case ZKL_3000_RC:
    case ZKL_3000_RCC:
    case ZKL_3000_RC_AFTC:
      return (
        <ZklrcVerifyStep
          device={device}
          composition={rest.composition}
          setComposition={rest.setComposition}
          resistanceBox={rest.resistanceBox}
          setResistanceBox={rest.setResistanceBox}
          jumperBox={rest.jumperBox}
          setJumperBox={rest.setJumperBox}
          ba={rest.ba}
          setBa={rest.setBa}
          dropOff={rest.dropOff}
          setDropOff={rest.setDropOff}
          setActiveStep={rest.setActiveStep}
          finishSelfTest={rest.finishSelfTest}
          lastJsonMessage={rest.lastJsonMessage}
        />
      )
    default:
      return (
        <Alert severity="error">
          {t('device.calibration.unsupportedDeviceType')}
        </Alert>
      )
  }
}

export default VerificationStep
